import { QuestionnaireItem } from '@medplum/fhirtypes'
import { Form, Row, Tooltip, Typography } from 'antd'
import styled from 'styled-components'
import { FC, useCallback, useContext, useEffect } from 'react'
import { RenderFunction, TooltipPlacement } from 'antd/es/tooltip'
import Markdown from 'react-markdown'
import { useAssessmentContext } from 'contexts/AssessmentContext'
import AssessmentFormContext from 'contexts/AssessmentFormContext'
import SanitizedInput from 'components/Common/Input/SanitizedInput'
import { getAnswerValuesFromItems, getValueFromEvent } from './helpers'
import { SocAssessmentQuestionnaireItem } from './SocAssessment/types/questionnaire'

interface FormComponentInterface {
  item: QuestionnaireItem
  meta: Partial<Record<SocAssessmentQuestionnaireItem, unknown>>
  disabled?: boolean
  callback: (arg0, arg1) => void
}

const { Text } = Typography

const IconTooltip: FC<{
  iconTooltipPlacement?: TooltipPlacement
  iconTooltipText: React.ReactNode | RenderFunction
  children: JSX.Element
  style?: React.CSSProperties
}> = ({ iconTooltipPlacement, iconTooltipText, children, style }) => {
  return children && iconTooltipText ? (
    <Tooltip
      title={iconTooltipText}
      placement={iconTooltipPlacement ?? 'right'}
      style={{ cursor: 'pointer', ...style }}
    >
      {children}
    </Tooltip>
  ) : (
    children
  )
}

const FormComponent = ({ item, meta, callback, disabled }: FormComponentInterface): JSX.Element => {
  const { cantEditAssessmentFields, assessmentMustBeFinalized, flatAnswers } =
    useAssessmentContext()
  const { form } = useContext(AssessmentFormContext)
  const parseValue = useCallback(
    (linkId: SocAssessmentQuestionnaireItem): unknown => {
      const result = getAnswerValuesFromItems(Object.values(flatAnswers), linkId)
      return result.length < 2 ? result[0] : result
    },
    [flatAnswers]
  )

  // Set values on change using the value from the controlled input instead of using
  // the value that's send trough the assessment context since are not updated in real
  // time but over a debounce(delays the execution of a function)
  const setValues = (e: Event): void => {
    callback(e, item.linkId)
    form?.setFieldValue(item.linkId as string, getValueFromEvent(e))
  }

  if (!item.linkId) {
    throw new Error('Missing required linkId')
  }

  // TOOD(ENG-978): We should not set state with useEffect. this is a ugly hack
  // to get the form to update the initial values, form object is only used
  // on Input components right now to pull patient chart info and populate the form,
  // we'll need to refactor this SOC assessment questionnaire as a whole to use antd's
  // Form component to fix it for good.
  useEffect(() => {
    if (form?.getFieldValue(item.linkId as string) === undefined) {
      const itemValue = parseValue(item.linkId as SocAssessmentQuestionnaireItem)

      if (itemValue) form?.setFieldValue(item.linkId as string, itemValue)
    }
  }, [form, item.linkId, parseValue])

  const {
    component: ComponentFromMetadata,
    hideText,
    hideQuestion,
    disabled: disabledFromMetadata,
    options,
    rules = [],
    tooltip,
    required,
    validateBeforeFinalization,
    enforceOptional = false,
    ...metadata
  } = meta[item.linkId] ?? {}

  const secondaryText = item?.extension?.[0]?.valueCodeableConcept?.coding?.[0]?.display

  if (hideQuestion) {
    return <></>
  }

  const formItemIsRequired =
    !enforceOptional &&
    (validateBeforeFinalization || assessmentMustBeFinalized) &&
    (item.required || required)
  return (
    <>
      {!hideText && (
        <HalfWidthContainer style={{ marginBottom: '8px' }}>
          <Row align="middle" style={{ gap: '6px' }}>
            <Text strong>{item.text}</Text>
            {tooltip && <IconTooltip iconTooltipText={tooltip.text}>{tooltip.icon}</IconTooltip>}
          </Row>
          <FormDescription text={secondaryText} />
        </HalfWidthContainer>
      )}
      <HalfWidthContainer>
        {!ComponentFromMetadata && (
          <Form.Item
            name={item.linkId}
            rules={[
              {
                required: formItemIsRequired,
                message: 'Please add a valid response'
              },
              ...rules
            ]}
            style={{ margin: 0 }}
          >
            <SanitizedInput
              allowClear
              onChange={setValues}
              disabled={disabledFromMetadata || cantEditAssessmentFields || disabled}
              {...metadata}
            />
          </Form.Item>
        )}
        {ComponentFromMetadata && (
          <Form.Item
            name={item.linkId}
            rules={[
              {
                required: formItemIsRequired,
                message: 'Please add a valid response'
              },
              ...rules
            ]}
            initialValue={parseValue(item.linkId as SocAssessmentQuestionnaireItem)}
            style={{ margin: 0 }}
          >
            <ComponentFromMetadata
              name={item.linkId}
              id={item.linkId}
              disabled={disabledFromMetadata || cantEditAssessmentFields || disabled}
              item={item}
              allowClear
              value={parseValue(item.linkId as SocAssessmentQuestionnaireItem)}
              onChange={setValues}
              options={
                options ??
                item?.answerOption?.map((option) => ({
                  label: option?.valueCoding?.display,
                  value: option?.valueCoding?.code ?? option?.valueCoding?.display
                }))
              }
              {...metadata}
            >
              {item.item?.map((i) => FormComponent({ item: i, meta, callback, disabled }))}
            </ComponentFromMetadata>
          </Form.Item>
        )}
      </HalfWidthContainer>
    </>
  )
}

const HalfWidthContainer = styled.div`
  width: 50%;
`

const SubTextContainer = styled.div`
  width: 85%;
`

export function FormDescription({ text }: { text: string | undefined }): JSX.Element {
  return (
    <SubTextContainer>
      <Text type="secondary" style={{ display: 'block' }}>
        <Markdown>{text}</Markdown>
      </Text>
    </SubTextContainer>
  )
}

export default FormComponent
